import React, { useEffect, useMemo, Children } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'

import { toast } from 'react-toastify'
import { useForm, FormProvider } from 'react-hook-form'
import { Box, DialogContent, Divider, IconButton, ListItemText, Tooltip, Typography, Stack, InputAdornment } from '@mui/material'
import Grid from '@mui/material/Grid'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CancelIcon from '@mui/icons-material/Cancel'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { formatCentsToDollars } from 'utils/price'
import { RECURRENCE } from 'settings/constants/service'
import { createDateFromString, formatDateToBEFormatDateFns, isValidDate } from 'utils/date'
import { useLazyGetServiceDetailsQuery } from 'api/configured-service/getServiceDetails'
import {
  useLazyGetRentalFeesByConfiguredServiceQuery,
  useAddRentalFeesToConfiguredServiceMutation,
  useUpdateRentalFeesConfiguredServiceMutation,
  useDeleteRentalFeesFromConfiguredServiceMutation,
} from 'api/configured-service/configuredServiceRentalFeesCrud'
import { useLazyGetRentalFeesQuery } from 'api/pricing/rentalFeesCrud'
import { sortByDateSelector } from 'data/utils/sortByDateSelector'
import { HHFormPriceField } from 'components/form-fields/v5'

import T from 'T'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import HHFormDesktopDatePicker from 'components/form-fields/v5/HHFormDesktopDatePicker'
import SkeletonListItem from './ListSkeleton'
import RentalFeeFormField from '../common/field/RentalFeeFormField'

const { RECURRING } = RECURRENCE

const MAXIMUM_RENTAL_FEES_ALLOWED = 1

const RentalFeesDialog = ({ isOpen = false, configuredServiceId, onClose, onSuccessCallback = noop }) => {
  const [getServiceDetails, { isFetching: isGetServiceDetailsLoading, data: csData, isSuccess: isSuccessGetServiceDetails }] =
    useLazyGetServiceDetailsQuery()
  const [getRentalFees, { isFetching: isGetRentalFeesLoading }] = useLazyGetRentalFeesQuery()
  const [
    getRentalFeesByConfiguredService,
    {
      isFetching: isGetRentalFeesByConfiguredServiceLoading,
      data: configuredRentalFeesData,
      isSuccess: isSuccessGetRentalFeesByConfiguredServiceLoading,
    },
  ] = useLazyGetRentalFeesByConfiguredServiceQuery()
  const [addRentalFeesToConfiguredService, { isLoading: isAddLoading }] = useAddRentalFeesToConfiguredServiceMutation()
  const [updateRentalFeesConfiguredService, { isLoading: isUpdateLoading }] = useUpdateRentalFeesConfiguredServiceMutation()
  const [deleteRentalFeesFromConfiguredService, { isLoading: isDeleteLoading }] = useDeleteRentalFeesFromConfiguredServiceMutation()

  const form = useForm({ defaultValues: { rentalFees: [] } })
  const {
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = form
  const rentalFees = watch('rentalFees')

  const configuredRentalFees = useMemo(() => get(configuredRentalFeesData, 'configuredRentalFees', []), [configuredRentalFeesData])
  const { serviceType, serviceName, price, containerCount, serviceStartDateBEFormat } = useMemo(
    () => ({
      pricedServiceId: get(csData, 'priceServiceDetail.id', ''),
      serviceType: get(csData, 'priceServiceDetail.serviceType', ''),
      serviceName: get(csData, 'priceServiceDetail.serviceName', ''),
      price: get(csData, 'priceServiceDetail.price', 0),
      containerCount: get(csData, 'advanced.newContainerCount', 0),
      serviceStartDateBEFormat: get(csData, 'schedule.startDate', ''),
    }),
    [csData]
  )
  const isRecurringService = useMemo(() => serviceType === RECURRING, [serviceType])
  const serviceStartDate = isRecurringService && serviceStartDateBEFormat ? createDateFromString(serviceStartDateBEFormat) : new Date()

  const isGetLoading = useMemo(
    () => isGetServiceDetailsLoading || isGetRentalFeesLoading || isGetRentalFeesByConfiguredServiceLoading,
    [isGetServiceDetailsLoading, isGetRentalFeesLoading, isGetRentalFeesByConfiguredServiceLoading]
  )

  const isSaveLoading = useMemo(() => isAddLoading || isUpdateLoading || isDeleteLoading, [isAddLoading, isUpdateLoading, isDeleteLoading])

  const handleRentalFeeChange = (index, rentalFee) => {
    setValue(`rentalFees.${index}.amount`, formatCentsToDollars(rentalFee.amountCents))
  }

  const validateStartDate = date => {
    return isValidDate(date) ? true : T.THIS_FIELD_IS_REQUIRED
  }

  const validateEndDate = (date, index) => {
    if (!date) {
      return true
    }

    if (!isValidDate(date)) {
      return T.INVALID_DATE
    }

    const startDate = get(rentalFees, `[${index}].startDate`, null)
    if (startDate && date < startDate) {
      return "End date can't be before start date"
    }
    return true
  }

  const handleClearEndDate = (event, index) => {
    event.stopPropagation()
    setValue(`rentalFees.${index}.endDate`, null)
  }

  const handleClear = index => {
    setValue(`rentalFees.${index}.rentalFeeId`, '', { shouldDirty: true })
    setValue(`rentalFees.${index}.startDate`, serviceStartDate)
    setValue(`rentalFees.${index}.endDate`, null)
    setValue(`rentalFees.${index}.amount`, 0)
  }

  const getInitialData = () => {
    getServiceDetails(configuredServiceId).unwrap().catch(handleError)
    getRentalFeesByConfiguredService({ id: configuredServiceId }).unwrap().catch(handleError)
  }

  const handleSave = async data => {
    const toBeAddedRentalFees = []
    const toBeUpdatedRentalFees = []
    const toBeDeletedRentalFees = []
    data.rentalFees.forEach(({ rentalFeeToConfiguredServiceId, rentalFeeId, startDate, endDate }) => {
      if (!rentalFeeToConfiguredServiceId && rentalFeeId) {
        // Add rental fee
        toBeAddedRentalFees.push({
          rentalFeeId,
          startDate: formatDateToBEFormatDateFns(startDate),
          endDate: formatDateToBEFormatDateFns(endDate),
        })
      }
      if (rentalFeeToConfiguredServiceId && rentalFeeId) {
        // Update rental fee
        toBeUpdatedRentalFees.push({
          rentalFeeToConfiguredServiceId,
          rentalFeeId,
          startDate: formatDateToBEFormatDateFns(startDate),
          endDate: formatDateToBEFormatDateFns(endDate),
        })
      }
      if (rentalFeeToConfiguredServiceId && !rentalFeeId) {
        // Delete rental fee
        toBeDeletedRentalFees.push(rentalFeeToConfiguredServiceId)
      }
    })

    const allPromises = []
    if (toBeDeletedRentalFees.length) {
      allPromises.push(
        deleteRentalFeesFromConfiguredService({
          id: configuredServiceId,
          rentalFeeToConfiguredServiceIds: toBeDeletedRentalFees,
        })
          .unwrap()
          .then(() => Promise.resolve({ success: true }))
          .catch(error => {
            handleError(error)
            return Promise.resolve({ error: true })
          })
      )
    }

    if (toBeAddedRentalFees.length) {
      allPromises.push(
        addRentalFeesToConfiguredService({ id: configuredServiceId, configuredRentalFees: toBeAddedRentalFees })
          .unwrap()
          .then(() => Promise.resolve({ success: true }))
          .catch(error => {
            handleError(error)
            return Promise.resolve({ error: true })
          })
      )
    }

    if (toBeUpdatedRentalFees.length) {
      allPromises.push(
        updateRentalFeesConfiguredService({ id: configuredServiceId, rentalFeesToConfiguredService: toBeUpdatedRentalFees })
          .unwrap()
          .then(() => Promise.resolve({ success: true }))
          .catch(error => {
            handleError(error)
            return Promise.resolve({ error: true })
          })
      )
    }

    const response = await Promise.all(allPromises)
    const hasError = response.find(({ error }) => error)
    if (hasError) {
      getInitialData()
      return
    }

    toast.success('Rental fees saved successfully')
    onSuccessCallback()
    onClose()
  }

  useEffect(() => {
    if (isOpen) {
      reset({ rentalFees: [] })
      getRentalFees().unwrap().catch(handleError)
      getInitialData()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isGetLoading && isSuccessGetServiceDetails && isSuccessGetRentalFeesByConfiguredServiceLoading) {
      const sortedConfiguredRentalFees = sortByDateSelector({ data: configuredRentalFees, key: 'createdAt', order: 'asc' })
      let newRentalFees = sortedConfiguredRentalFees.map(({ id, rentalFeeId, startDate, endDate, amountCents }) => ({
        rentalFeeToConfiguredServiceId: id,
        rentalFeeId,
        startDate: createDateFromString(startDate),
        endDate: createDateFromString(endDate),
        amount: formatCentsToDollars(amountCents),
      }))

      // newRentalFees.length < containerCount
      if (newRentalFees.length < MAXIMUM_RENTAL_FEES_ALLOWED) {
        newRentalFees = newRentalFees.concat(
          // containerCount - newRentalFees.length
          Array(MAXIMUM_RENTAL_FEES_ALLOWED).fill({ rentalFeeId: '', startDate: serviceStartDate, amount: 0 })
        )
      }

      reset({ rentalFees: newRentalFees })
    }
  }, [isGetLoading, isSuccessGetServiceDetails, isSuccessGetRentalFeesByConfiguredServiceLoading])

  return (
    <HHBaseDialog fullWidth maxWidth="sm" open={isOpen} onClose={onClose}>
      <HHDialogTitle title={T.RENTAL_FEES} onClose={onClose} />
      <DialogContent sx={{ px: 0 }}>
        {isGetLoading && <SkeletonListItem rows={1} />}
        {!isGetLoading && (
          <FormProvider {...form}>
            <ListItemText
              sx={{ px: 3 }}
              primary={
                <Typography variant="h4" fontWeight={600} noWrap>
                  {`${serviceName} | `}
                  <HHDisplayMoney value={price} formatToDollars={false} />
                </Typography>
              }
              secondary={
                <Typography variant="h5" fontWeight={500} color="textSecondary" display="flex" alignItems="center" gap={0.5}>
                  <Tooltip arrow placement="bottom" title="The number of rental fees cannot exceed the number of containers">
                    <InfoOutlinedIcon color="action" />
                  </Tooltip>
                  {`${containerCount} container${containerCount === 1 ? '' : 's'}`}
                </Typography>
              }
              slotProps={{
                primary: { variant: 'h4', fontWeight: 600, noWrap: true },

                secondary: {
                  variant: 'h5',
                  fontWeight: 500,
                  color: 'textSecondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                },
              }}
            />
            <Divider sx={{ my: 2 }} />
            <Box sx={{ px: 3 }}>
              {Children.toArray(
                rentalFees.map((rentalFee, index) => {
                  const rentalFeeId = get(rentalFee, 'rentalFeeId', '')
                  const startDateErrorMessage = get(errors, `rentalFees.${index}.startDate.message`)
                  const startDateError = Boolean(startDateErrorMessage)

                  const endDate = get(rentalFee, 'endDate')
                  const endDateErrorMessage = get(errors, `rentalFees.${index}.endDate.message`)
                  const endDateError = Boolean(endDateErrorMessage)

                  return (
                    <Stack direction="column" rowGap={2} mb={2}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h4" fontWeight={600}>
                          {`Rental fee ${index + 1}`}
                        </Typography>
                        {rentalFeeId && (
                          <IconButton onClick={() => handleClear(index)}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <RentalFeeFormField
                        name={`rentalFees.${index}.rentalFeeId`}
                        value={rentalFeeId}
                        onChange={newRentalFee => handleRentalFeeChange(index, newRentalFee)}
                      />
                      {rentalFeeId && (
                        <>
                          <Grid container spacing={2}>
                            <Grid
                              size={{
                                sm: 12,
                                md: 6,
                              }}
                            >
                              <HHFormDesktopDatePicker
                                name={`rentalFees.${index}.startDate`}
                                control={control}
                                label={T.START_DATE}
                                rules={{ required: T.THIS_FIELD_IS_REQUIRED, validate: validateStartDate }}
                                TextFieldProps={{
                                  deprecatedLabel: false,
                                  error: startDateError,
                                  helperText: startDateErrorMessage,
                                }}
                                {...(isRecurringService && { minDate: serviceStartDate })}
                              />
                            </Grid>
                            <Grid
                              size={{
                                sm: 12,
                                md: 6,
                              }}
                            >
                              <HHFormDesktopDatePicker
                                name={`rentalFees.${index}.endDate`}
                                control={control}
                                label={T.END_DATE}
                                rules={{ validate: date => validateEndDate(date, index) }}
                                TextFieldProps={{
                                  deprecatedLabel: false,
                                  helperText: endDateError ? endDateErrorMessage : T.OPTIONAL,
                                  InputProps: {
                                    ...(endDate && {
                                      endAdornment: (
                                        <InputAdornment position="end" onClick={event => handleClearEndDate(event, index)}>
                                          <ClearOutlinedIcon />
                                        </InputAdornment>
                                      ),
                                    }),
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>

                          <HHFormPriceField
                            fullWidth
                            name={`rentalFees.${index}.amount`}
                            control={control}
                            deprecatedLabel={false}
                            label={T.AMOUNT}
                            disabled
                          />
                        </>
                      )}
                    </Stack>
                  )
                })
              )}
            </Box>
          </FormProvider>
        )}
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton
          loadingPosition="center"
          loading={isSaveLoading}
          disabled={isSaveLoading}
          label={T.SAVE}
          onClick={handleSubmit(handleSave)}
        />
      </HHDialogActions>
    </HHBaseDialog>
  )
}

RentalFeesDialog.propTypes = {
  isOpen: PropTypes.bool,
  configuredServiceId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onSuccessCallback: PropTypes.func,
}

export default RentalFeesDialog
